import {
  Attachment,
  Claim,
  ClaimStatus,
  ProcedureAttachment,
} from '@dataTypes/claimFeature.types'
import moment from 'moment'
import { fileFromUrl } from './file.utils'

/** RFC 4122 UUID v4 pattern (matches notes API `context_id` for claims). */
const CLAIM_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * Returns the claim UUID to use as `context_id` for notes with `context_type === claims`.
 * Prefers `claim_id`; falls back to `id` only when it is already a UUID (API-mapped rows).
 */
export const getNotesClaimContextId = (claim: Claim): string | undefined => {
  const fromField = claim.claim_id?.trim()
  if (fromField && CLAIM_UUID_RE.test(fromField)) return fromField
  const id = claim.id?.trim()
  if (id && CLAIM_UUID_RE.test(id)) return id
  return undefined
}

/** Display id for note overlay header: `Notes for {value}` — carrier id, then UI id, then API UUID. */
export const getClaimIdForNotesHeader = (claim: Claim): string | null => {
  const pick = (value: string | undefined): string | undefined => {
    const t = value?.trim()
    return t && t.length > 0 ? t : undefined
  }
  const resolved =
    pick(claim.providerClaimId) ?? pick(claim.id) ?? pick(claim.claim_id)
  return resolved ?? null
}

/**
 * Reads `claim_id` from create-claim API response bodies (wrapped or unwrapped).
 */
export const extractClaimIdFromCreateClaimResponse = (
  payload: unknown,
): string | undefined => {
  const readClaimId = (value: unknown): string | undefined => {
    if (!value || typeof value !== 'object') return undefined
    const claimId = (value as Record<string, unknown>).claim_id
    return typeof claimId === 'string' && CLAIM_UUID_RE.test(claimId)
      ? claimId
      : undefined
  }

  const readFirstFromArray = (value: unknown): string | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined
    return readClaimId(value[0])
  }

  if (!payload) return undefined

  // Shape: [{ claim_id, ... }]
  const directArrayClaimId = readFirstFromArray(payload)
  if (directArrayClaimId) return directArrayClaimId

  if (typeof payload !== 'object') return undefined
  const root = payload as Record<string, unknown>

  // Shape: { claim_id, ... }
  const directClaimId = readClaimId(root)
  if (directClaimId) return directClaimId

  // Shape: { data: [...] } or { data: { ... } }
  const data = root.data
  const dataArrayClaimId = readFirstFromArray(data)
  if (dataArrayClaimId) return dataArrayClaimId
  const dataClaimId = readClaimId(data)
  if (dataClaimId) return dataClaimId

  // Shape: { claim: { claims: [...] } } and nested variants.
  const containers: unknown[] = [root, data]
  for (const container of containers) {
    if (!container || typeof container !== 'object') continue
    const claimBlock = (container as Record<string, unknown>).claim
    if (!claimBlock || typeof claimBlock !== 'object') continue
    const claims = (claimBlock as Record<string, unknown>).claims
    const nestedClaimId = readFirstFromArray(claims)
    if (nestedClaimId) return nestedClaimId
  }

  return undefined
}

const mapClaimStatusToString = (status: ClaimStatus): string => {
  switch (status) {
    case ClaimStatus.UnderReview:
      return 'Under Review'
    case ClaimStatus.Approved:
      return 'Approved'
    case ClaimStatus.Rejected:
      return 'Rejected'
    case ClaimStatus.PartiallyApproved:
      return 'Partially Approved'
    case ClaimStatus.Cancelled:
      return 'Cancelled'
    default:
      return 'Pending'
  }
}

export const mapClaimStatus = (
  statusCode: string,
): { status: string; description: string } => {
  switch (statusCode) {
    // Approved Statuses (A2 - A8)
    case 'A2':
      return {
        status: 'Partially Approved',
        description: 'Acknowledgement/Acceptance into adjudication system.',
      }
    case 'A3':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Returned as unprocessable claim.',
      }
    case 'A4':
      return { status: 'Rejected', description: 'Acknowledgement/Not Found.' }
    case 'A6':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for Missing Information.',
      }
    case 'A7':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for Invalid Information.',
      }
    case 'A8':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for relational field in error.',
      }
    case 'A0':
      return {
        status: 'Under Review',
        description: 'Acknowledgement/Forwarded.',
      }
    case 'A1':
      return { status: 'Under Review', description: 'Acknowledgement/Receipt.' }
    case 'A5':
      return {
        status: 'Under Review',
        description: 'Acknowledgement/Split Claim.',
      }

    // DR Statuses (DR02 - DR08)
    case 'DR02':
      return {
        status: 'Approved',
        description:
          'Acknowledgement/Acceptance into the data reporting/processing system.',
      }
    case 'DR03':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Returned as unprocessable claim.',
      }
    case 'DR04':
      return { status: 'Rejected', description: 'Acknowledgement/Not Found.' }
    case 'DR05':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for Missing Information.',
      }
    case 'DR06':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for invalid information.',
      }
    case 'DR07':
      return {
        status: 'Rejected',
        description: 'Acknowledgement/Rejected for relational field in error.',
      }
    case 'DR01':
      return { status: 'Under Review', description: 'Acknowledgement/Receipt.' }
    case 'DR08':
      return { status: 'Under Review', description: 'Acknowledgement/Warning.' }

    // Pending Statuses (P0 - P5)
    case 'P0':
      return {
        status: 'Under Review',
        description: 'Pending: Adjudication/Details.',
      }
    case 'P1':
      return { status: 'Under Review', description: 'Pending/In Process.' }
    case 'P2':
      return { status: 'Under Review', description: 'Pending/Payer Review.' }
    case 'P3':
      return {
        status: 'Under Review',
        description: 'Pending/Provider Requested Information.',
      }
    case 'P4':
      return {
        status: 'Under Review',
        description: 'Pending/Patient Requested Information.',
      }
    case 'P5':
      return {
        status: 'Under Review',
        description: 'Pending/Payer Administrative/System hold.',
      }

    // Finalized Statuses (F1 - F3)
    case 'F1':
      return {
        status: 'Approved',
        description: 'Finalized/Payment - The claim/line has been paid.',
      }
    case 'F3F':
      return {
        status: 'Approved',
        description:
          'Finalized/Forwarded - The claim/encounter processing has been completed. Any applicable payment has been made, and the claim/encounter has been forwarded.',
      }
    case 'F3N':
      return {
        status: 'Approved',
        description:
          'Finalized/Not Forwarded - The claim/encounter processing has been completed. Any applicable payment has been made, but it has not been forwarded.',
      }
    case 'F2':
      return {
        status: 'Rejected',
        description: 'Finalized/Denial - The claim/line has been denied.',
      }
    case 'F4':
      return {
        status: 'Rejected',
        description:
          'Finalized/Adjudication Complete - No payment forthcoming - The claim/encounter has been adjudicated, and no further payment will be made.',
      }
    case 'F0':
      return {
        status: 'Under Review',
        description:
          'Finalized - The claim/encounter has completed the adjudication cycle and no more action will be taken.',
      }
    case 'F3':
      return {
        status: 'Under Review',
        description:
          'Finalized/Revised - Adjudication information has been changed.',
      }

    // Requests for Additional Information (R0 - R17)
    case 'R0':
      return {
        status: 'Under Review',
        description: 'Requests for additional Information/General Requests.',
      }
    case 'R1':
      return {
        status: 'Under Review',
        description: 'Requests for additional Information/Entity Requests.',
      }
    case 'R3':
      return {
        status: 'Under Review',
        description: 'Requests for additional Information/Claim/Line.',
      }
    case 'R4':
      return {
        status: 'Under Review',
        description: 'Requests for additional Information/Documentation.',
      }
    case 'R5':
      return {
        status: 'Under Review',
        description: 'Request for additional information/more specific detail.',
      }
    case 'R6':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Regulatory requirements.',
      }
    case 'R7':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Confirm care is consistent with Health Plan policy coverage.',
      }
    case 'R8':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Confirm care is consistent with health plan coverage exceptions.',
      }
    case 'R9':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Determination of medical necessity.',
      }
    case 'R10':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Support a filed grievance or appeal.',
      }
    case 'R11':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Pre-payment review of claims.',
      }
    case 'R12':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Clarification or justification of use for a specified procedure code.',
      }
    case 'R13':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Original documents submitted are not readable.',
      }
    case 'R14':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Original documents received are not what was requested.',
      }
    case 'R15':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Workers Compensation coverage determination.',
      }
    case 'R16':
      return {
        status: 'Under Review',
        description:
          'Requests for additional information – Eligibility determination.',
      }
    case 'R17':
      return {
        status: 'Under Review',
        description: 'Replacement of a Prior Request.',
      }

    // Rejected Statuses (E0 - E4)
    case 'E0':
      return {
        status: 'Rejected',
        description: 'Response not possible - error on submitted request data.',
      }
    case 'E1':
      return {
        status: 'Rejected',
        description: 'Response not possible - System Status.',
      }
    case 'E2':
      return {
        status: 'Rejected',
        description:
          'Information Holder is not responding; resubmit at a later time.',
      }
    case 'E3':
      return {
        status: 'Rejected',
        description: 'Correction required - relational fields in error.',
      }
    case 'E4':
      return {
        status: 'Rejected',
        description:
          'Trading partner agreement specific requirement not met: Data correction required.',
      }

    // Data Search Unsuccessful (D0)
    case 'D0':
      return {
        status: 'Rejected',
        description: 'Data Search Unsuccessful - The payer.',
      }

    default:
      return { status: 'Unknown', description: 'Unknown status code.' }
  }
}

export const attachmentTypeOptions = [
  {
    value: 'B4',
    label: 'Referral Form',
  },
  {
    value: 'DA',
    label: 'Dental Models',
  },
  {
    value: 'DG',
    label: 'Diagnostic Report',
  },
  {
    value: 'EB',
    label: 'Explanation of Benefts',
  },
  {
    value: 'OZ',
    label: 'Other Attachments',
  },
  {
    value: 'P6',
    label: 'Periodontal Charts',
  },
  {
    value: 'RB',
    label: 'X-Rays',
  },
  {
    value: 'RR',
    label: 'Radiology Reports',
  },
  {
    value: 'XP',
    label: 'Intra-Oral Photograph',
  },
  {
    value: '03',
    label: 'Narrative',
  },
]

export const getAttachmentType = (key: string): string => {
  switch (key) {
    case 'narrative':
      return '03'
    case 'xray':
      return 'RB'
    case 'perioChart':
      return 'P6'
    case 'other':
      return 'OZ'
    default:
      return ''
  }
}

export const updateAndCompleteAttachmentsData = async (
  attachments: Attachment[] | ProcedureAttachment[],
): Promise<Attachment[] | ProcedureAttachment[]> => {
  const attachmentsUpdated = await Promise.all(
    attachments.map(async (attachment: Attachment | ProcedureAttachment) => {
      const file = await fileFromUrl(
        attachment.fileUrl!,
        attachment.fileName!,
        (() => {
          if (attachment.mimeType) {
            return attachment.mimeType
          } else {
            switch (attachment.fileName?.split('.').pop()?.toUpperCase()) {
              case 'PDF':
                return 'application/pdf'
              case 'JPG':
                return 'image/jpeg'
              case 'PNG':
                return 'image/png'
            }
          }
        })() as string,
      )
      return {
        ...attachment,
        newFile: file,
        ...(!attachment.mimeType && { mimeType: file.type }),
        ...(!attachment.size && { size: file.size }),
      }
    }),
  )
  return attachmentsUpdated as Attachment[] | ProcedureAttachment[]
}

const getItemApprovedAmount = (
  procedureCode: string,
  claimStatus: any,
): any => {
  const itemApprovedAmount =
    claimStatus?.claimstatusitems?.find(
      (item: any) => item.procedure_code === procedureCode,
    )?.item_status_paid_amount || 0
  return itemApprovedAmount
}

export const filteredClaimsData = (
  claims: any,
  activeTab?: string,
  searchQuery?: string,
  patientId?: string,
): any[] => {
  // Guard: API may return error string when backend is unavailable
  if (
    !claims ||
    typeof claims !== 'object' ||
    !('claims' in claims) ||
    !Array.isArray(claims.claims)
  ) {
    return []
  }

  {
    const claimsData = claims.claims
      .filter((claim: any) => {
        if (patientId) {
          return claim.patient_id === patientId
        }
        return true
      })
      .map((claim: any) => {
        const procedures = claim.claimitems.map((procedure: any) => {
          const procSettings = claim.appointment_id
            ? procedure.procedure_log.procedure.procedure_settings[0]
            : procedure.treatment_plan_item.procedure_settings[0]
          const attachments = procedure.claimitemattachments.map(
            (attachment: any, index: number) => {
              return {
                id: attachment.claim_item_attachment_id,
                itemNo: `CPA-${index + 1}`,
                item: attachment.file.original_name,
                formatType: attachment.file.mime_type.split('/')[1],
                attachmentType: attachment.type,
                orientation: 'Portrait',
                dateTaken: moment(attachment.date_time_created_at).format(
                  'MM/DD/YYYY',
                ),
                fileName: attachment.file.original_name,
                mimeType: attachment.file.mime_type,
                size: attachment.file.file_size,
                isEditing: false,
                fileUrl: attachment.file.file_path,
              }
            },
          )
          return {
            id: procedure.claim_item_id,
            procedureId: claim.appointment_id
              ? procedure.procedure_log.procedure.procedure_id
              : procedure.treatment_plan_item.procedure_settings.procedure
                  .procedure_id,
            procedureName: claim.appointment_id
              ? procedure.procedure_log.procedure.description
              : procedure.treatment_plan_item.procedure_settings.procedure
                  .description,
            procedureCode: claim.appointment_id
              ? procedure.procedure_log.procedure.procedure_code
              : procedure.treatment_plan_item.procedure_settings.procedure
                  .procedure_code,
            ...(procedure?.procedure_log?.proc_date && {
              procedureDate: moment(procedure.procedure_log.proc_date).format(
                'MM/DD/YYYY',
              ),
            }),
            ...(procedure?.treatment_plan_item?.treatment_plan_phase && {
              phaseName:
                procedure.treatment_plan_item.treatment_plan_phase.phase_name,
              procedureDate: moment(
                procedure.treatment_plan_item.treatment_plan_phase
                  .scheduled_date,
              ).format('MM/DD/YYYY'),
            }),
            procLogId: procedure.proc_log_id,
            itemId: procedure.item_id,
            provider: `Dr. ${claim.provider.first_name} ${claim.provider.last_name}`,
            providerFirstName: claim.provider.first_name,
            providerLastName: claim.provider.last_name,
            coverage: '',
            amount: procedure.amount,
            insurance_plan_id: procedure.insurance_plan_id || null,
            clinic_fee: procedure.clinic_fee || null,
            allowed_amount: procedure.allowed_amount || null,
            write_off: procedure.write_off || null,
            estimated_insurance_pay: procedure.estimated_insurance_pay || null,
            estimated_patient_resp: procedure.estimated_patient_resp || null,
            amountPaid: getItemApprovedAmount(
              claim.appointment_id
                ? procedure.procedure_log.procedure.procedure_code
                : procedure.treatment_plan_item.procedure_settings.procedure
                    .procedure_code,
              claim.claimstatus?.[0] || [],
            ),
            siteRegion: claim.appointment_id
              ? procedure.procedure_log.area_type === 1
                ? `${procedure.procedure_log.tooth}${procedure.procedure_log.surf ? '/' + procedure.procedure_log.surf : ''}`
                : procedure.procedure_log.area
              : procedure.treatment_plan_item.surface,
            toothNumber: '15',
            toothSurface: 'FM',
            status: 'Unclaimed',
            source: 'Treatment Plan',
            procedureNotes: procedure.notes,
            procedureAttachments: attachments,
            patientName: 'Pippa Wilkinson',
          }
        })

        const attachments = claim.claimattachments.map(
          (attachment: any, index: number) => {
            return {
              id: attachment.claim_attachment_id,
              itemNo: `CA-${index + 1}`,
              item: attachment.file.original_name,
              formatType: attachment.file.mime_type.split('/')[1],
              attachmentType: attachment.type,
              orientation: 'Portrait',
              dateTaken: moment(attachment.date_time_created_at).format(
                'MM/DD/YYYY',
              ),
              fileName: attachment.file.original_name,
              mimeType: attachment.file.mime_type,
              size: attachment.file.file_size,
              isEditing: false,
              fileUrl: attachment.file.file_path,
              notes: [],
            }
          },
        )

        const subscriberDateOfBirth = moment(
          claim.insuranceplan.subscriber.date_of_birth,
        ).format('MM/DD/YYYY')

        return {
          id: claim.claim_id,
          claim_id: claim.claim_id,
          createdAt: moment(claim.date_time_created_at).format('MM/DD/YYYY'),
          updatedAt: moment(claim.date_time_updated_at).format('MM/DD/YYYY'),
          patientIdSystem: claim.patient.patient_id,
          patientId: claim.patient_id,
          patientMemberId: claim.insuranceplan.patient.member_id,
          patientName: claim.patient.first_name + ' ' + claim.patient.last_name,
          patientFirstName: claim.patient.first_name,
          patientLastName: claim.patient.last_name,
          patientDateOfBirth: moment(claim.patient.date_of_birth).format(
            'MM/DD/YYYY',
          ),
          patientGender: claim.patient.gender,
          relationToSubscriber: 'Self',
          subscriberId: claim.insuranceplan.subscriber.subscriber_id,
          subscriberName:
            claim.insuranceplan.subscriber.first_name +
            ' ' +
            claim.insuranceplan.subscriber.last_name,
          subscriberFirstName: claim.insuranceplan.subscriber.first_name,
          subscriberLastName: claim.insuranceplan.subscriber.last_name,
          subscriberDateOfBirth: subscriberDateOfBirth,
          entityType: claim.provider_entity_type,
          providerEntityType: claim.provider_entity_type,
          providerId: claim.provider.provider_id,
          providerName: `Dr. ${claim.provider.first_name} ${claim.provider.last_name}`,
          providerTaxId: claim.provider.tax_id_number,
          clinicName: 'Bright Smile Dental',
          groupName: claim.insuranceplan.group_name,
          groupId: claim.insuranceplan.group_no,
          insurancePlanId: claim.insuranceplan.insurance_plan_id,
          insuranceProviderId: claim.insuranceplan.carrier.carrier_id,
          insuranceProvider: claim.insuranceplan.carrier.carrier_name,
          insuranceCarrierId: claim.insuranceplan.carrier.carrier_id,
          insuranceCarrier: claim.insuranceplan.carrier.carrier_name,
          insuranceCarrierPayerCode: claim.insuranceplan.carrier.payer_id,
          appointmentId: claim.appointment_id,
          treatmentPlanId: claim.treatment_plan_id,
          status: claim?.claimstatus?.[0]?.claimstatuscodes?.[0]?.code || '',
          generalStatus: claim.is_draft
            ? 'Draft'
            : (mapClaimStatus(
                claim?.claimstatus?.[0]?.claimstatuscodes?.[0]?.code || '',
              )?.status as ClaimStatus),
          checkStatus: '',
          checkNumber: '',
          claimSource: claim.treatment_plan_id ? 1 : 0,
          claimType: claim.claim_type,
          claimForm: 'ADA 2025',
          claimAmount: claim.claim_amount,
          amountPaid: claim?.claimstatus?.[0]?.claim_status_paid_amount || '',
          procedures: procedures,
          attachments: attachments,
          notes: claim.notes,
          providerClaimId: claim.public_id,
        }
      })

    if (activeTab === undefined && searchQuery === undefined) {
      return claimsData
    } else {
      return claimsData.filter((claim: any) => {
        const statusText = claim.generalStatus.toLowerCase()
        if (activeTab === 'pre-auth' && claim.claimType !== 'Pre-Authorization')
          return false
        if (activeTab === 'claims' && claim.claimType !== 'Claims') return false
        const matchesSearchQuery = [
          claim.id,
          claim.providerClaimId,
          claim.patientName,
          claim.insuranceCarrier,
          statusText,
        ].some((field) =>
          field
            ?.toString()
            .toLowerCase()
            .includes(searchQuery?.toLowerCase() || ''),
        )
        return matchesSearchQuery
      })
    }
  }
}

export const filteredTrackingData = (claims: any): any[] => {
  if (
    !claims ||
    typeof claims !== 'object' ||
    !('tracking' in claims) ||
    !Array.isArray(claims.tracking)
  ) {
    return []
  }

  return claims.tracking || []
}

export const isUUID = (id?: string): boolean => {
  if (!id) return false
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(id)
}
